import { Button, Checkbox, TextField, useAlert } from '@ads/components-react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SerieViewModel } from '@/shared/api/generated';
import { Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { useCompanyById } from '../../../hooks/useCompanyById';
import { useSaveSerie } from '../../../hooks/useSaveSerie';
import { type SerieFormValues, serieFormSchema } from '../../../types/serieForm';
import { ClassStudentsSection } from '../../molecules/ClassStudentsSection';
import { CompanyAutocomplete } from '../../molecules/CompanyAutocomplete';

export interface SerieFormProps {
  /** Série em edição; `null` para criação. */
  readonly serie: SerieViewModel | null;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

const DEFAULT_VALUES: SerieFormValues = {
  companyId: undefined,
  title: '',
  description: '',
  general: false,
  active: true,
  thumbnailFile: null,
  removeThumbnail: false,
  classStudents: [],
};

/**
 * Formulário de Série (criar/editar), usado em tela dedicada (não modal). Um
 * único formulário cobre os dois modos: `serie === null` abre vazio (com
 * `Ativo` ligado); caso contrário, é pré-preenchido a partir do `SerieViewModel`.
 *
 * A seção "Turmas" só aparece quando `Geral` está desmarcado (ver
 * `ClassStudentsSection`) e opera inteiramente em estado local — persiste
 * somente quando este formulário é salvo. Fiel aos frames *Série · Criar/Editar*
 * de `design/series.pen`, incluindo os botões soltos abaixo do card.
 */
export function SerieForm({ serie, onSaved, onCancel }: SerieFormProps) {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const { save, isPending } = useSaveSerie();
  const fileInputId = useId();

  const isEdit = serie?.id != null;
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  // Rótulo de exibição da empresa selecionada — não vem de `SerieViewModel` (só
  // expõe `companyId`), então em edição buscamos o nome via `useCompanyById`
  // (`/v1/company?CompanyId=`) para pré-preencher o autocomplete.
  const [companyLabel, setCompanyLabel] = useState<string | undefined>(undefined);
  const { companyName } = useCompanyById(serie?.companyId ?? undefined);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SerieFormValues>({
    resolver: zodResolver(serieFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(
      serie
        ? {
            companyId: serie.companyId ?? undefined,
            title: serie.title ?? '',
            description: serie.resumee ?? '',
            general: serie.isGeneral ?? false,
            active: serie.active ?? true,
            thumbnailFile: null,
            removeThumbnail: false,
            classStudents: (serie.classStudents ?? []).map(cs => ({
              classStudentId: cs.classStudentId ?? 0,
              description: cs.description ?? '',
            })),
          }
        : DEFAULT_VALUES,
    );
    setCompanyLabel(undefined);
  }, [serie, reset]);

  useEffect(() => {
    if (companyName) setCompanyLabel(companyName);
  }, [companyName]);

  const thumbnailFile = watch('thumbnailFile');
  const removeThumbnail = watch('removeThumbnail');
  const general = watch('general');
  const classStudents = watch('classStudents');

  // Preview do novo arquivo escolhido; revoga a URL anterior ao trocar/desmontar.
  useEffect(() => {
    if (!thumbnailFile) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  const existingThumbnailUrl =
    isEdit && serie?.thumbnail && !removeThumbnail ? serie.thumbnail : undefined;
  const displayedThumbnailUrl = previewUrl ?? existingThumbnailUrl;

  const onSubmit = handleSubmit(values => {
    save(values, serie?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'series.toast.updateSuccess' : 'series.toast.createSuccess'),
        });
        onSaved();
      },
      onError: () => alert({ alertType: 'warning', title: t('series.toast.saveError') }),
    });
  });

  return (
    <form className="flex flex-col gap-xs" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
        <h2 className="ts-heading-sm text-ctx-content-title">
          {t(isEdit ? 'series.form.editTitle' : 'series.form.createTitle')}
        </h2>

        <Controller
          control={control}
          name="companyId"
          render={({ field }) => (
            <CompanyAutocomplete
              value={field.value ?? null}
              {...(companyLabel !== undefined ? { selectedLabel: companyLabel } : {})}
              onChange={(id, label) => {
                field.onChange(id ?? undefined);
                setCompanyLabel(label || undefined);
              }}
              label={t('series.form.company')}
              placeholder={t('series.form.companyPlaceholder')}
            />
          )}
        />

        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <TextField
              label={`${t('series.form.title')}*`}
              placeholder={t('series.form.titlePlaceholder')}
              value={field.value}
              onChange={e => field.onChange(e.target.value)}
              size="md"
              fullWidth
              hasError={Boolean(errors.title)}
              {...(errors.title ? { helper: t('series.form.errors.titleRequired') } : {})}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <FormField label={`${t('series.form.description')}*`}>
              <Textarea
                placeholder={t('series.form.descriptionPlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                rows={4}
                {...(errors.description
                  ? { error: t('series.form.errors.descriptionRequired') }
                  : {})}
              />
            </FormField>
          )}
        />

        <div className="flex flex-col gap-nano">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
            {t('series.form.thumbnail')}
          </span>
          {displayedThumbnailUrl ? (
            <div className="flex flex-col items-start gap-nano">
              <img
                src={displayedThumbnailUrl}
                alt={t('series.form.thumbnail')}
                className="h-[140px] w-[200px] rounded-sm object-cover"
              />
              <Button
                hierarchy="danger"
                size="md"
                type="button"
                onClick={() => {
                  setValue('thumbnailFile', null);
                  setValue('removeThumbnail', true);
                }}
              >
                {t('series.form.removePhoto')}
              </Button>
            </div>
          ) : (
            <label
              htmlFor={fileInputId}
              className="flex h-[112px] w-[200px] cursor-pointer items-center justify-center rounded-sm border-hairline border-ctx-layout-border bg-base-gray-100"
            >
              <PhotoIcon className="h-xxl w-xxl text-ctx-content-placeholder" />
            </label>
          )}
          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={e => {
              const picked = e.target.files?.[0];
              if (picked) {
                setValue('thumbnailFile', picked);
                setValue('removeThumbnail', false);
              }
              e.target.value = '';
            }}
          />
        </div>

        <div className="flex items-center gap-xs">
          <Controller
            control={control}
            name="general"
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                size="md"
                label={t('series.form.general')}
                onCheckedChange={checked => {
                  if (checked !== 'indeterminate') field.onChange(checked);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                size="md"
                label={t('series.form.active')}
                onCheckedChange={checked => {
                  if (checked !== 'indeterminate') field.onChange(checked);
                }}
              />
            )}
          />
        </div>

        {!general && (
          <ClassStudentsSection
            classStudents={classStudents}
            onAdd={classStudent => setValue('classStudents', [...classStudents, classStudent])}
            onRemove={classStudentId =>
              setValue(
                'classStudents',
                classStudents.filter(cs => cs.classStudentId !== classStudentId),
              )
            }
          />
        )}
      </div>

      <div className="flex items-center justify-end gap-nano">
        <Button hierarchy="secondary" size="md" type="button" onClick={onCancel}>
          {t('series.form.cancel')}
        </Button>
        <Button
          hierarchy="primary"
          size="md"
          type="submit"
          isLoading={isPending}
          disabled={isPending}
        >
          {t('series.form.save')}
        </Button>
      </div>
    </form>
  );
}
